/**
 * Utility functions for date and time operations
 */

/**
 * Check if a task is overdue based on date and time
 * @param {Object} task - Task object with due_date and optional time
 * @returns {boolean} - True if the task is overdue
 */
export const isTaskOverdue = (task) => {
  if (!task.due_date || task.completed) {
    return false;
  }

  const now = new Date();
  
  // If task has a specific time, combine date and time for comparison
  if (task.time) {
    // Parse the due date and combine with time
    const dueDate = new Date(task.due_date);
    const [hours, minutes] = task.time.split(':').map(Number);
    
    // Create a complete datetime for due date + time
    const dueDateWithTime = new Date(dueDate);
    dueDateWithTime.setHours(hours, minutes, 0, 0);
    
    return now > dueDateWithTime;
  } else {
    // If no specific time, task is overdue if current date has passed the due date
    const dueDate = new Date(task.due_date);
    const today = new Date();
    
    // Set both dates to start of day for comparison (ignore time)
    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    return today > dueDate;
  }
};

/**
 * Get task status (completed, overdue, pending)
 * @param {Object} task - Task object
 * @returns {string} - Status string: 'completed', 'overdue', or 'pending'
 */
export const getTaskStatus = (task) => {
  if (task.completed) {
    return 'completed';
  }
  
  if (isTaskOverdue(task)) {
    return 'overdue';
  }
  
  return 'pending';
};

/**
 * Get background color class based on task status
 * @param {Object} task - Task object
 * @returns {string} - CSS class for background color
 */
export const getTaskStatusStyle = (task) => {
  const status = getTaskStatus(task);
  
  switch (status) {
    case 'completed':
      return 'bg-[#e2ffe2]'; // Light green
    case 'overdue':
      return 'bg-[#ffe2e2]'; // Light red
    case 'pending':
    default:
      return 'bg-[#e2e2ff]'; // Light blue
  }
};